import { DeviceRow, httpFetchDevices } from "api/admin/device";
import { useState } from "react";
import { useQuery } from "react-query";
import {
  DeviceBadge,
  DeviceDetailsOverlay,
  deviceDateLabel,
  deviceOsLabel,
} from "./DeviceDetailsPanel";

const linkedDeviceLimit = 20;

const LinkedDevices = ({ userId }: { userId: number }) => {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const { data, isLoading, isError } = useQuery<{
    devices: DeviceRow[];
    total: number;
  }>(
    ["devices", "user", userId],
    () =>
      httpFetchDevices({
        page: 1,
        limit: linkedDeviceLimit,
        userId,
      }),
    { enabled: Number.isInteger(userId) && userId > 0 },
  );
  const devices = data?.devices || [];

  return (
    <section className="pt-6 mt-8 border-t border-orange-200">
      <div className="flex items-center justify-between gap-3">
        <h5 className="font-semibold text-gray-900">
          Linked devices ({data?.total ?? "—"})
        </h5>
        {data && data.total > linkedDeviceLimit ? (
          <span className="text-xs text-gray-500">
            Most recent {linkedDeviceLimit}
          </span>
        ) : null}
      </div>

      <div className="mt-3 overflow-hidden bg-white border border-orange-200 divide-y divide-orange-100 rounded-xl">
        {isLoading ? (
          <p className="p-4 text-sm text-gray-500">Loading linked devices…</p>
        ) : isError ? (
          <p className="p-4 text-sm text-red-700">
            Linked devices could not be loaded.
          </p>
        ) : devices.length ? (
          devices.map((device) => (
            <button
              key={device.publicId}
              type="button"
              onClick={() => setSelectedId(device.publicId)}
              className="flex w-full items-center justify-between gap-3 p-3 text-left transition hover:bg-orange-50 focus:bg-orange-50 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-orange-300"
            >
              <span className="min-w-0">
                <span className="block text-sm font-medium text-gray-900 truncate">
                  {device.metadata.model || "Unknown model"}
                </span>
                <span className="block mt-0.5 text-xs text-gray-500">
                  {deviceOsLabel(device)} · Last seen{" "}
                  {deviceDateLabel(device.lastSeenAt)}
                </span>
              </span>
              <span className="flex shrink-0 items-center gap-2">
                <DeviceBadge value={device.status} />
                <DeviceBadge value={device.integrityStatus} />
                <span aria-hidden="true" className="text-gray-400">
                  ›
                </span>
              </span>
            </button>
          ))
        ) : (
          <p className="p-4 text-sm text-gray-500">
            No linked devices recorded.
          </p>
        )}
      </div>

      {selectedId ? (
        <DeviceDetailsOverlay
          publicId={selectedId}
          onClose={() => setSelectedId(null)}
        />
      ) : null}
    </section>
  );
};

export default LinkedDevices;
